import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, List, X } from "@phosphor-icons/react";
import { auth, appUserAuth } from "../lib/api";
import { useAppAuthed } from "../lib/gate";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/clarity-score", label: "Clarity Score" },
  { to: "/learn", label: "Learn" },
  { to: "/tax-buckets", label: "Tax Buckets" },
  { to: "/series", label: "Series" },
  { to: "/wealth-implementation", label: "Implementation" },
  { to: "/infinite-banking", label: "Wealth for Life" },
  { to: "/calculator", label: "Calculator" },
  { to: "/policy-audit", label: "Policy Audit" },
  { to: "/goals", label: "Goal Lab" },
  { to: "/quiz", label: "Find My Fit" },
  { to: "/results", label: "Results" },
  { to: "/reserve", label: "Book a call" },
  { to: "/support", label: "Support" },
];

export default function Nav() {
  const loc = useLocation();
  const nav = useNavigate();
  const isDash = loc.pathname.startsWith("/dashboard");
  const isHome = loc.pathname === "/";
  const logged = auth.isLoggedIn();
  const appAuthed = useAppAuthed();
  const appUser = appAuthed ? appUserAuth.fullName() : null;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);

  // Auto-hide on scroll down, reveal on scroll up (mobile UX).
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY.current;
      // Small threshold so tiny scroll jitters don't flap the header
      if (Math.abs(delta) < 6) return;
      if (delta > 0 && y > 80) setHidden(true);   // scrolling down past the hero band → hide
      else setHidden(false);                       // scrolling up → show
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  const goBack = () => {
    if (window.history.length > 1) nav(-1);
    else nav("/");
  };

  return (
    <>
      <header
        data-testid="primary-nav"
        className={`fixed top-0 inset-x-0 z-50 bg-exodus-bg/95 backdrop-blur-md border-b border-exodus-border transition-transform duration-300 ${
          hidden ? "-translate-y-full" : "translate-y-0"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
          {/* Left: Back + Logo */}
          <div className="flex items-center gap-3 min-w-0">
            {!isHome && (
              <button
                type="button"
                data-testid="nav-back-btn"
                onClick={goBack}
                aria-label="Go back"
                className="inline-flex items-center justify-center w-9 h-9 border border-exodus-border text-exodus-muted hover:text-exodus-gold hover:border-exodus-gold transition-colors shrink-0"
              >
                <ArrowLeft size={16} weight="bold" />
              </button>
            )}
            <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 min-w-0">
              <span className="font-display text-2xl tracking-tight gold-text">EXODUS</span>
              {isHome && <span className="hidden min-[1800px]:inline eyebrow text-exodus-muted whitespace-nowrap">Life &amp; Wealth</span>}
            </Link>
          </div>

          {/* Center: Desktop links — only on true widescreen (2xl / 1536px+) */}
          {!isDash && (
            <nav className="hidden 2xl:flex items-center gap-4 flex-1 justify-center">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-sm tracking-wide text-exodus-text hover:text-exodus-gold transition-colors whitespace-nowrap"
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          )}

          {/* Right: Auth OR Hamburger (mobile + tablet + laptop up to 2xl) */}
          <div className="flex items-center gap-3 shrink-0">
            {logged ? (
              <>
                <Link to="/dashboard" data-testid="nav-dashboard" className="text-sm gold-text hover:underline whitespace-nowrap">
                  Dashboard
                </Link>
                <button
                  data-testid="nav-logout"
                  onClick={() => { auth.logout(); nav("/"); }}
                  className="text-sm text-exodus-muted hover:text-exodus-text"
                >
                  Logout
                </button>
              </>
            ) : appUser ? (
              <span data-testid="nav-app-user" className="hidden md:inline text-xs text-exodus-muted whitespace-nowrap">
                Hi, <span className="text-exodus-text">{appUser.split(" ")[0]}</span>
              </span>
            ) : null}

            {/* Hamburger — visible below 2xl (mobile, tablet, laptop) */}
            <button
              type="button"
              data-testid="nav-hamburger"
              aria-label="Open menu"
              onClick={() => setDrawerOpen(true)}
              className="2xl:hidden inline-flex items-center justify-center w-10 h-10 border border-exodus-border text-exodus-text hover:text-exodus-gold hover:border-exodus-gold transition-colors"
            >
              <List size={20} weight="bold" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div
          data-testid="nav-mobile-drawer"
          className="fixed inset-0 z-[60] 2xl:hidden"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <button
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />
          {/* Panel */}
          <div className="absolute inset-y-0 right-0 w-[85%] max-w-sm bg-exodus-bg border-l border-exodus-gold-deep/40 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-exodus-border">
              <span className="font-display text-xl gold-text">EXODUS</span>
              <button
                data-testid="nav-drawer-close"
                aria-label="Close menu"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 inline-flex items-center justify-center border border-exodus-border text-exodus-muted hover:text-exodus-gold hover:border-exodus-gold transition-colors"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 py-6 space-y-1">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-drawer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`block px-4 py-3 rounded-md text-base tracking-wide transition-colors ${
                    loc.pathname === l.to
                      ? "bg-exodus-gold text-exodus-bg"
                      : "text-exodus-text hover:bg-exodus-surface hover:text-exodus-gold"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
            </nav>
            <div className="px-6 py-5 border-t border-exodus-border">
              {logged ? (
                <button
                  data-testid="nav-drawer-logout"
                  onClick={() => { auth.logout(); setDrawerOpen(false); nav("/"); }}
                  className="w-full text-sm text-exodus-muted hover:text-exodus-text py-3"
                >
                  Log out
                </button>
              ) : appUser ? (
                <div className="space-y-2 text-center">
                  <p className="text-xs text-exodus-muted">Signed in as</p>
                  <p className="text-sm text-exodus-text">{appUser}</p>
                  <button
                    data-testid="nav-drawer-lock"
                    onClick={() => {
                      appUserAuth.lock();
                      setDrawerOpen(false);
                      window.location.reload();
                    }}
                    className="w-full text-xs eyebrow gold-text hover:underline pt-3"
                  >
                    Lock app
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  data-testid="nav-drawer-advisor"
                  className="block w-full text-center text-xs eyebrow text-exodus-muted hover:text-exodus-gold py-3"
                >
                  Advisor sign-in
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
